import {
  mapQueryDsrDefaultDatasources,
  mapQueryDsrWFEmailTemplates,
  mutationSaveWorkflow,
  queryDsrDefaultDatasources,
  queryDsrWFEmailTemplates,
  queryDsrWorkflows,
  mapDsrWorkflows,
  mutationUpdateWorkflow,
  mutationDeleteWorkflow,
  queryDsrWorkflowById,
  mapQueryWorkflowById
} from './queries'
import {
  BlockType,
  DSRFormData,
  dsrWFDefaultDatasourceType,
  initialDSRWorkflowState,
  initialDSRWorkflowStepData,
  Workflow
} from './createEditWorkflow/types'
import { getInitialConfig } from './createEditWorkflow/utils'
import graphqlService from '../../../services/graphqlService'
import { DsrEmailTemplateTypes } from '../dsrEmailTemplates/dsrEmailTemplatesSlice'
import { DsrRequestType } from '../../../services/graphqlSchemaTypes'
import { API_DSR_REQUEST_TYPES } from '../../../constants'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

export enum EmailTemplatesTypes {
  REQUEST_SUBMITTED = 'requestSubmittedEmailTemplates',
  EMAIL_VERIFICATION_SUCCESSFUL = 'emailVerificationEmailTemplates',
  REQUEST_APPROVED = 'requestApprovalEmailTemplates',
  REPORT_SHARED = 'reportSharingEmailTemplates',
  REPORT_PASSWORD = 'reportPasswordEmailTemplates',
  REQUEST_CLOSED = 'closureEmailTemplates'
}

export enum DataDiscoveryActionTypes {
  AUTO_DISCOVER_AND_VALIDATE = 'AUTO_DISCOVER_AND_VALIDATE',
  AUTO_DISCOVER_AND_ERASE = 'AUTO_DISCOVER_AND_ERASE',
  AUTO_DISCOVER_AND_RECTIFY = 'AUTO_DISCOVER_AND_RECTIFY',
  MANUALLY_DISCOVER_AND_VALIDATE = 'MANUALLY_DISCOVER_AND_VALIDATE',
  DISCOVER_ONLY = 'DISCOVER_ONLY'
}

export enum DataDiscoveryActionTakenBy {
  REQUEST_OWNER = 'DSR_OWNER',
  DATA_SOURCE_OWNER = 'DATA_SOURCE_OWNER'
}

export const requestTypeToActions = {
  [DsrRequestType.Access]: [
    DataDiscoveryActionTypes.AUTO_DISCOVER_AND_VALIDATE,
    DataDiscoveryActionTypes.MANUALLY_DISCOVER_AND_VALIDATE,
    DataDiscoveryActionTypes.DISCOVER_ONLY
  ],
  [DsrRequestType.Erasure]: [
    DataDiscoveryActionTypes.AUTO_DISCOVER_AND_VALIDATE,
    DataDiscoveryActionTypes.AUTO_DISCOVER_AND_ERASE,
    DataDiscoveryActionTypes.DISCOVER_ONLY,
    DataDiscoveryActionTypes.MANUALLY_DISCOVER_AND_VALIDATE
  ],
  [DsrRequestType.Rectification]: [
    DataDiscoveryActionTypes.AUTO_DISCOVER_AND_VALIDATE,
    DataDiscoveryActionTypes.DISCOVER_ONLY,
    DataDiscoveryActionTypes.MANUALLY_DISCOVER_AND_VALIDATE
  ],
  [DsrRequestType.DoNotSell]: [
    DataDiscoveryActionTypes.AUTO_DISCOVER_AND_VALIDATE,
    DataDiscoveryActionTypes.MANUALLY_DISCOVER_AND_VALIDATE,
    DataDiscoveryActionTypes.DISCOVER_ONLY
  ],
  [DsrRequestType.OptOut]: [
    DataDiscoveryActionTypes.AUTO_DISCOVER_AND_VALIDATE,
    DataDiscoveryActionTypes.MANUALLY_DISCOVER_AND_VALIDATE,
    DataDiscoveryActionTypes.DISCOVER_ONLY
  ],
  [DsrRequestType.Other]: [
    DataDiscoveryActionTypes.AUTO_DISCOVER_AND_VALIDATE,
    DataDiscoveryActionTypes.MANUALLY_DISCOVER_AND_VALIDATE,
    DataDiscoveryActionTypes.DISCOVER_ONLY
  ]
}

export const ACTION_DSR_FETCH_EMAIL_TEMPLATES = 'dsr/fetch/email-templates'
export const fetchDsrWFEmailTemplates = createAsyncThunk(
  ACTION_DSR_FETCH_EMAIL_TEMPLATES,
  async ({
    eventType,
    requestType,
    id
  }: {
    eventType: DsrEmailTemplateTypes
    requestType: API_DSR_REQUEST_TYPES
    id: string
  }) => {
    const resultRaw = await graphqlService.execute(
      queryDsrWFEmailTemplates({ eventType, requestType })
    )
    return { templates: mapQueryDsrWFEmailTemplates(resultRaw), eventType, id }
  }
)

export const ACTION_DSR_DEFAULT_DATASOURCES_QUERY = 'dsr/wf/fetch/default-datasources'
export const fetchWFDsrDefaultDatasources = createAsyncThunk(
  ACTION_DSR_DEFAULT_DATASOURCES_QUERY,
  async ({ requestType, id }: { requestType: DsrRequestType; id: string }) => {
    const resultRaw = await graphqlService.execute(queryDsrDefaultDatasources())
    return { defaultDatasources: mapQueryDsrDefaultDatasources(resultRaw, requestType), id }
  }
)

export type DsrWorkflowParams = {
  workflow: Workflow
  blockConfig: DSRFormData
}
export const ACTION_SAVE_DSR_WORKFLOW = 'dsr/wf/save'
export const saveDsrWorkflow = createAsyncThunk(
  ACTION_SAVE_DSR_WORKFLOW,
  async (params: DsrWorkflowParams, { rejectWithValue }) => {
    try {
      return await graphqlService.execute(mutationSaveWorkflow(params))
    } catch (error: any) {
      return rejectWithValue({ statusMessage: error?.errors[0]?.message || '' })
    }
  }
)

export const ACTION_DELETE_WORKFLOW = 'dsr/wf/delete'
export const deleteDsrWorkflow = createAsyncThunk(
  ACTION_DELETE_WORKFLOW,
  async (workflowId: string, { rejectWithValue }) => {
    try {
      return await graphqlService.execute(mutationDeleteWorkflow(workflowId))
    } catch (error: any) {
      return rejectWithValue({ statusMessage: error?.errors[0]?.message || '' })
    }
  }
)

export const ACTION_UPDATE_DSR_WORKFLOW = 'dsr/wf/update'
export const updateDsrWorkflow = createAsyncThunk(
  ACTION_UPDATE_DSR_WORKFLOW,
  async (workflowData: DsrWorkflowParams, { rejectWithValue }) => {
    try {
      const resultRaw = await graphqlService.execute(mutationUpdateWorkflow(workflowData))
      return resultRaw
    } catch (error: any) {
      return rejectWithValue({ statusMessage: error?.errors[0]?.message || '' })
    }
  }
)

export type DetailedWorkflow = Workflow & {
  updatedAt: string
  createdAt: string
  createdBy: string
  associatedFormsCount: number
}

type EmailTemplates = {
  id: string
  name: string
}

export const EmailTemplateStateKeys = {
  [DsrEmailTemplateTypes.requestSubmitted]: 'requestSubmittedEmailTemplates',
  [DsrEmailTemplateTypes.emailVerificationSuccess]: 'emailVerificationEmailTemplates',
  [DsrEmailTemplateTypes.requestApproved]: 'requestApprovalEmailTemplates',
  [DsrEmailTemplateTypes.reportShared]: 'reportSharingEmailTemplates',
  [DsrEmailTemplateTypes.reportPassword]: 'reportPasswordEmailTemplates',
  [DsrEmailTemplateTypes.requestClosed]: 'closureEmailTemplates'
}

export enum DsrWorkflowSaveStatus {
  SUCCESS = 'SUCCESS',
  UPDATE_SUCCESS = 'UPDATE_SUCCESS',
  ERROR = 'ERROR'
}

type dsrWorkflowAutomationState = {
  workflows: {
    list: WorkflowListItem[]
    totalCount: number
  }
  stepData: {
    stepEmailIdVerification: {
      [id: string]: {
        requestSubmittedEmailTemplates: EmailTemplates[]
        emailVerificationEmailTemplates: EmailTemplates[]
      }
    }
    stepRequestApproval: {
      [id: string]: {
        requestApprovalEmailTemplates: EmailTemplates[]
      }
    }
    stepDataDiscoveryValidation: {
      [id: string]: {
        dsrDefaultDatasources: dsrWFDefaultDatasourceType[]
      }
    }
    stepReportGeneration: {
      [id: string]: {
        reportSharingEmailTemplates: EmailTemplates[]
        reportPasswordEmailTemplates: EmailTemplates[]
        closureEmailTemplates: EmailTemplates[]
      }
    }
  }
  saveDsrWorkflowStatus?: DsrWorkflowSaveStatus
  currentCreatedWorkflowId?: string
  isDeleteWorkflowModalOpened?: boolean
  isUpdateWorkflowModalOpened?: boolean
  workflowToDeleteId?: string
  workflowToUpdateId?: string
  requestTypeOfWorkflowToUpdate?: RequestType
  activeWorkflow: Workflow
  workflowBlockConfig: DSRFormData
}

const activeWorkflowInitialState: Workflow = {
  workflowAutomationName: '',
  workflowAutomationNameError: false,
  currenUserEmailId: '',
  workflowAutomationId: '',
  stepEmailIdVerification: {},
  stepRequestApproval: {},
  stepDataDiscoveryValidation: {},
  stepReportGeneration: {}
}

const initialState: dsrWorkflowAutomationState = {
  activeWorkflow: activeWorkflowInitialState,
  workflows: {
    list: [],
    totalCount: 0
  },
  stepData: {
    stepEmailIdVerification: {},
    stepRequestApproval: {},
    stepDataDiscoveryValidation: {},
    stepReportGeneration: {}
  },
  isDeleteWorkflowModalOpened: false,
  isUpdateWorkflowModalOpened: false,
  workflowToDeleteId: '',
  workflowToUpdateId: '',
  workflowBlockConfig: getInitialConfig()
}

export type RequestType =
  | 'ACCESS'
  | 'ERASURE'
  | 'RECTIFICATION'
  | 'DO_NOT_SELL'
  | 'OPT_OUT'
  | 'OTHER'

export type DataSubjectRequestForm = {
  id: string
  formGroupId: string
  name: string
}

export type WorkflowListItem = {
  id: string
  name: string
  isDeleted: boolean
  createdAt: string
  lastUpdated: string
  createdBy: string
  requestType: RequestType
  attachedDsrFormCount?: number
}

export const ACTION_FETCH_WORKFLOWS = 'get-workflows'
export const fetchWorkflows = createAsyncThunk(ACTION_FETCH_WORKFLOWS, async () => {
  const resultRaw = await graphqlService.execute(queryDsrWorkflows())
  return mapDsrWorkflows(resultRaw)
})

export const ACTION_FETCH_DETAILED_ACTIVE_WORKFLOW = 'get-detailed-active-workflow'
export const fetchDetailedActiveWorkflow = createAsyncThunk(
  ACTION_FETCH_DETAILED_ACTIVE_WORKFLOW,
  async (workflowId: string) => {
    const resultRaw = await graphqlService.execute(queryDsrWorkflowById(workflowId))
    return mapQueryWorkflowById(resultRaw)
  }
)

const dsrWorkflowAutomationSlice = createSlice({
  name: 'dsrWorkflowAutomation',
  initialState,
  reducers: {
    setActiveWorkflow: (state, { payload }) => {
      state.activeWorkflow = payload
    },
    setActionToBeTaken: (state, { payload }) => {
      // Make a shallow copy of the dsrDefaultDatasources array
      const updatedDatasources = state.activeWorkflow?.stepDataDiscoveryValidation?.[
        payload.blockId
      ]?.dsrDefaultDatasources.map((ds) => {
        if (ds.id === payload.id) {
          let updatedDatasource = { ...ds, actionToBeTaken: payload.actionToBeTaken }
          // Check the condition for actionToBeTaken and set actionToBeTakenBy if it matches
          if (
            payload.actionToBeTaken === DataDiscoveryActionTypes.MANUALLY_DISCOVER_AND_VALIDATE ||
            payload.actionToBeTaken === DataDiscoveryActionTypes.DISCOVER_ONLY
          ) {
            updatedDatasource = {
              ...updatedDatasource,
              actionToBeTakenBy: DataDiscoveryActionTakenBy.DATA_SOURCE_OWNER
            }
          }
          return updatedDatasource
        }
        // Otherwise, return the original object
        return ds
      })
      // // Update the state immutably
      if (updatedDatasources) {
        state.activeWorkflow.stepDataDiscoveryValidation[
          payload.blockId
        ].dsrDefaultDatasources = updatedDatasources
      }
    },
    setActionToBeTakenBy: (state, { payload }) => {
      const updatedDatasources = state.activeWorkflow?.stepDataDiscoveryValidation?.[
        payload.blockId
      ]?.dsrDefaultDatasources.map((ds) => {
        if (ds.id === payload.id) {
          const updatedDatasource = { ...ds, actionToBeTakenBy: payload.actionToBeTakenBy }
          return updatedDatasource
        }
        // Otherwise, return the original object
        return ds
      })
      // Update the state immutably
      if (updatedDatasources) {
        state.activeWorkflow.stepDataDiscoveryValidation[
          payload.blockId
        ].dsrDefaultDatasources = updatedDatasources
      }
    },
    setWorkflowAutomationName: (state, { payload }) => {
      state.activeWorkflow.workflowAutomationName = payload
    },
    updateEmailIdVerificationStepData: (state, { payload }) => {
      state.activeWorkflow.stepEmailIdVerification[payload.id] = payload.emailWorkflowData
      state.stepData.stepEmailIdVerification[payload.id] = payload.emailStepData
    },
    updateRequestApprovalStepData: (state, { payload }) => {
      state.activeWorkflow.stepRequestApproval[payload.id] = payload.requestApprovalWorkflowData
      state.stepData.stepRequestApproval[payload.id] = payload.requestApprovalStepData
    },
    updateDataDiscoveryValidationStepData: (state, { payload }) => {
      state.activeWorkflow.stepDataDiscoveryValidation[payload.id] =
        payload.dataDiscoveryValidationWorkflowData
      state.stepData.stepDataDiscoveryValidation[payload.id] =
        payload.dataDiscoveryValidationStepData
    },
    updateReportGenerationStepData: (state, { payload }) => {
      state.activeWorkflow.stepReportGeneration[payload.id] = payload.reportGenerationWorkflowData
      state.stepData.stepReportGeneration[payload.id] = payload.reportGenerationStepData
    },
    setRequestSubmittedEmailTemplateId: (state, { payload }) => {
      const { value, id } = payload
      state.activeWorkflow.stepEmailIdVerification[id].requestSubmittedEmailTemplateId = value
    },
    setEmailVerificationSuccessEmailTemplateId: (state, { payload }) => {
      const { value, id } = payload
      state.activeWorkflow.stepEmailIdVerification[
        id
      ].emailVerificationSuccessEmailTemplateId = value
    },
    setEmailIdVerificationAcknowledgement: (state, { payload }) => {
      const { value, id } = payload
      state.activeWorkflow.stepEmailIdVerification[id].sendAcknowledgement = value
    },
    setRequestApprovalEmailTemplateId: (state, { payload }) => {
      const { value, id } = payload
      state.activeWorkflow.stepRequestApproval[id].requestApprovalEmailTemplateId = value
    },
    setApproveRequest: (state, { payload }) => {
      const { value, id } = payload
      state.activeWorkflow.stepRequestApproval[id].approveRequest = value
    },
    setSendApprovalEmail: (state, { payload }) => {
      const { value, id } = payload
      state.activeWorkflow.stepRequestApproval[id].sendApprovalEmail = value
    },
    setSendAdditionalEmails: (state, { payload }) => {
      const { value, id, stateKey } = payload
      state.activeWorkflow[stateKey][id].sendAdditionalEmails = value
    },
    setSendAdditionalEmailsForReportSharing: (state, { payload }) => {
      const { value, id } = payload
      state.activeWorkflow.stepReportGeneration[id].sendAdditionalEmailsForReportSharing = value
    },
    setAdditionalEmailFieldsForReportSharing: (state, { payload }) => {
      const { value, id } = payload
      state.activeWorkflow.stepReportGeneration[id].additionalEmailFieldsForReportSharing = value
    },
    setAdditionalEmailFieldsForReportSharingError: (state, { payload }) => {
      const { value, id } = payload
      state.activeWorkflow.stepReportGeneration[
        id
      ].additionalEmailFieldsForReportSharingError = value
    },
    setAdditionalEmailTemplateId: (state, { payload }) => {
      const { value, id, stateKey } = payload
      state.activeWorkflow[stateKey][id].additionalEmailTemplateId = value
    },
    setAdditionalEmailFields: (state, { payload }) => {
      const { value, id, stateKey } = payload
      state.activeWorkflow[stateKey][id].additionalEmailFields = value
    },
    setAdditionalEmailTemplateError: (state, { payload }) => {
      const { value, id, stateKey } = payload
      state.activeWorkflow[stateKey][id].additionalEmailTemplateError = value
    },
    setAdditionalEmailFieldsError: (state, { payload }) => {
      const { value, id, stateKey } = payload
      state.activeWorkflow[stateKey][id].additionalEmailFieldsError = value
    },
    setShareReportWithDataSubject: (state, { payload }) => {
      const { value, id } = payload
      state.activeWorkflow.stepReportGeneration[id].shareReportWithDataSubject = value
    },
    setShareReportEmailTemplateId: (state, { payload }) => {
      const { value, id } = payload
      state.activeWorkflow.stepReportGeneration[id].shareReportEmailTemplateId = value
    },
    setReportPasswordEmailTemplateId: (state, { payload }) => {
      const { value, id } = payload
      state.activeWorkflow.stepReportGeneration[id].reportPasswordEmailTemplateId = value
    },
    setAutomaticallyCloseRequest: (state, { payload }) => {
      const { value, id } = payload
      state.activeWorkflow.stepReportGeneration[id].automaticallyCloseRequest = value
    },
    setSendClosureEmail: (state, { payload }) => {
      const { value, id } = payload
      state.activeWorkflow.stepReportGeneration[id].sendClosureEmail = value
    },
    setClosureEmailTemplateId: (state, { payload }) => {
      const { value, id } = payload
      state.activeWorkflow.stepReportGeneration[id].closureEmailTemplateId = value
    },
    setRequestSubmittedEmailTemplateError: (state, { payload }) => {
      const { value, id } = payload
      state.activeWorkflow.stepEmailIdVerification[id].requestSubmittedEmailTemplateError = value
    },
    setEmailVerificationSuccessEmailTemplateError: (state, { payload }) => {
      const { value, id } = payload
      state.activeWorkflow.stepEmailIdVerification[
        id
      ].emailVerificationSuccessEmailTemplateError = value
    },
    setRequestApprovalEmailTemplateError: (state, { payload }) => {
      const { value, id } = payload
      state.activeWorkflow.stepRequestApproval[id].requestApprovalEmailTemplateError = value
    },
    setClosureEmailTemplateError: (state, { payload }) => {
      const { value, id } = payload
      state.activeWorkflow.stepReportGeneration[id].closureEmailTemplateError = value
    },
    setReportPasswordEmailTemplateError: (state, { payload }) => {
      const { value, id } = payload
      state.activeWorkflow.stepReportGeneration[id].reportPasswordEmailTemplateError = value
    },
    setReportSharingEmailTemplateError: (state, { payload }) => {
      const { value, id } = payload
      state.activeWorkflow.stepReportGeneration[id].reportSharingEmailTemplateError = value
    },
    setWorkflowToDeleteId: (state, { payload }) => {
      state.isDeleteWorkflowModalOpened = payload.isDeleteWorkflowModalOpened
      state.workflowToDeleteId = payload.id
    },
    setWorkflowToUpdateId: (state, { payload }) => {
      state.isUpdateWorkflowModalOpened = payload.isUpdateWorkflowModalOpened
      state.workflowToUpdateId = payload.id
      state.requestTypeOfWorkflowToUpdate = payload.requestTypeOfWorkflowToUpdate
    },
    setWorkflowAutomationNameError: (state, { payload }) => {
      state.activeWorkflow.workflowAutomationNameError = payload
    },
    setActiveWorkflowDefaultData: (state, { payload }) => {
      state.activeWorkflow.requestType = payload.requestType
      state.activeWorkflow.currenUserEmailId = payload.currenUserEmailId
    },
    resetActiveDsrWorkflow: (state) => {
      state.activeWorkflow = activeWorkflowInitialState
      state.saveDsrWorkflowStatus = initialState.saveDsrWorkflowStatus
      state.workflowBlockConfig = []
      state.stepData = initialState.stepData
    },
    initializeActiveWorkflow: (state, { payload }: { payload: DSRFormData }) => {
      payload.forEach((block) => {
        switch (block.type) {
          case BlockType.STEP_EMAIL_ID_VERIFICATION:
            state.activeWorkflow.stepEmailIdVerification[block.data?.id ?? ''] =
              initialDSRWorkflowState.stepEmailIdVerification
            state.stepData.stepEmailIdVerification[block.data?.id ?? ''] =
              initialDSRWorkflowStepData.stepEmailIdVerification
            break
          case BlockType.STEP_REQUEST_APPROVAL:
            state.activeWorkflow.stepRequestApproval[block.data?.id ?? ''] =
              initialDSRWorkflowState.stepRequestApproval
            state.stepData.stepRequestApproval[block.data?.id ?? ''] =
              initialDSRWorkflowStepData.stepRequestApproval
            break
          case BlockType.STEP_DATA_DISCOVERY_VALIDATION:
            state.activeWorkflow.stepDataDiscoveryValidation[block.data?.id ?? ''] =
              initialDSRWorkflowState.stepDataDiscoveryValidation
            state.stepData.stepDataDiscoveryValidation[block.data?.id ?? ''] =
              initialDSRWorkflowStepData.stepDataDiscoveryValidation
            break
          case BlockType.STEP_REPORT_GENERATION:
            state.activeWorkflow.stepReportGeneration[block.data?.id ?? ''] =
              initialDSRWorkflowState.stepReportGeneration
            state.stepData.stepReportGeneration[block.data?.id ?? ''] =
              initialDSRWorkflowStepData.stepReportGeneration
            break
        }
      })
    },
    updateActiveWorkflow: (state, { payload }: { payload: DSRFormData }) => {
      payload.forEach((block) => {
        switch (block.type) {
          case BlockType.STEP_EMAIL_ID_VERIFICATION: {
            const stepDataIds = Object.keys(state.stepData.stepEmailIdVerification)
            const lastId = stepDataIds[stepDataIds.length - 1]
            const emailStepData = state.stepData.stepEmailIdVerification[lastId]

            state.activeWorkflow.stepEmailIdVerification = {
              ...state.activeWorkflow.stepEmailIdVerification,
              [block.data?.id ?? '']: {
                ...initialDSRWorkflowState.stepEmailIdVerification,
                requestSubmittedEmailTemplateId:
                  emailStepData?.requestSubmittedEmailTemplates?.[0]?.id,
                emailVerificationSuccessEmailTemplateId:
                  emailStepData.emailVerificationEmailTemplates?.[0]?.id,
                sendAcknowledgement: true
              }
            }
            state.stepData.stepEmailIdVerification = {
              ...state.stepData.stepEmailIdVerification,
              [block.data?.id ?? '']: initialDSRWorkflowStepData.stepEmailIdVerification
            }
            break
          }
          case BlockType.STEP_REQUEST_APPROVAL: {
            const approvalStepDataIds = Object.keys(state.stepData.stepRequestApproval)
            const lastApprovalId = approvalStepDataIds[approvalStepDataIds.length - 1]
            const approvalStepData = state.stepData.stepRequestApproval[lastApprovalId]

            state.activeWorkflow.stepRequestApproval = {
              ...state.activeWorkflow.stepRequestApproval,
              [block.data?.id ?? '']: {
                ...initialDSRWorkflowState.stepRequestApproval,
                requestApprovalEmailTemplateId:
                  approvalStepData?.requestApprovalEmailTemplates?.[0]?.id,
                approveRequest: true,
                sendApprovalEmail: true
              }
            }
            state.stepData.stepRequestApproval = {
              ...state.stepData.stepRequestApproval,
              [block.data?.id ?? '']: initialDSRWorkflowStepData.stepRequestApproval
            }
            break
          }
          case BlockType.STEP_DATA_DISCOVERY_VALIDATION: {
            const discoveryStepDataIds = Object.keys(
              state.activeWorkflow.stepDataDiscoveryValidation
            )
            const lastDiscoveryId = discoveryStepDataIds[discoveryStepDataIds.length - 1]
            const discoveryStepData =
              state.activeWorkflow.stepDataDiscoveryValidation[lastDiscoveryId]

            state.activeWorkflow.stepDataDiscoveryValidation = {
              ...state.activeWorkflow.stepDataDiscoveryValidation,
              [block.data?.id ?? '']: {
                ...initialDSRWorkflowState.stepDataDiscoveryValidation,
                dsrDefaultDatasources:
                  discoveryStepData?.dsrDefaultDatasources ||
                  initialDSRWorkflowState.stepDataDiscoveryValidation.dsrDefaultDatasources
              }
            }
            state.stepData.stepDataDiscoveryValidation = {
              ...state.stepData.stepDataDiscoveryValidation,
              [block.data?.id ?? '']: initialDSRWorkflowStepData.stepDataDiscoveryValidation
            }
            break
          }
          case BlockType.STEP_REPORT_GENERATION: {
            const reportStepDataIds = Object.keys(state.stepData.stepReportGeneration)
            const lastReportId = reportStepDataIds[reportStepDataIds.length - 1]
            const reportStepData = state.stepData.stepReportGeneration[lastReportId]

            state.activeWorkflow.stepReportGeneration = {
              ...state.activeWorkflow.stepReportGeneration,
              [block.data?.id ?? '']: {
                ...initialDSRWorkflowState.stepReportGeneration,
                shareReportEmailTemplateId: reportStepData?.reportSharingEmailTemplates?.[0]?.id,
                reportPasswordEmailTemplateId:
                  reportStepData?.reportPasswordEmailTemplates?.[0]?.id,
                closureEmailTemplateId: reportStepData?.closureEmailTemplates?.[0]?.id,
                shareReportWithDataSubject: true,
                automaticallyCloseRequest: true,
                sendClosureEmail: true
              }
            }
            state.stepData.stepReportGeneration = {
              ...state.stepData.stepReportGeneration,
              [block.data?.id ?? '']: initialDSRWorkflowStepData.stepReportGeneration
            }
            break
          }
        }
      })
    },
    setWorkflowBlockConfig: (state, { payload }: { payload: DSRFormData }) => {
      state.workflowBlockConfig = payload
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchDsrWFEmailTemplates.fulfilled, (state, action) => {
      const { templates, eventType, id } = action.payload
      const stateKey = EmailTemplateStateKeys[eventType]

      if (stateKey) {
        const stepKey = Object.keys(state.stepData).find((step) => {
          return Object.keys(state?.stepData?.[step]?.[id] ?? {}).includes(stateKey)
        })

        if (stepKey) {
          state.stepData[stepKey][id][stateKey] = templates
        }
      }
    })
    builder.addCase(fetchWFDsrDefaultDatasources.fulfilled, (state, { payload }) => {
      state.activeWorkflow.stepDataDiscoveryValidation[payload.id].dsrDefaultDatasources =
        payload.defaultDatasources
    })
    builder.addCase(saveDsrWorkflow.fulfilled, (state, { payload }) => {
      if (payload?.createFlow?.flowId) {
        state.saveDsrWorkflowStatus = DsrWorkflowSaveStatus.SUCCESS
        state.currentCreatedWorkflowId = payload?.createFlow?.flowId
      } else if (payload?.updateDsrWorkflowConfig?.flowId) {
        state.saveDsrWorkflowStatus = DsrWorkflowSaveStatus.UPDATE_SUCCESS
      } else {
        state.saveDsrWorkflowStatus = DsrWorkflowSaveStatus.ERROR
      }
    })
    builder.addCase(updateDsrWorkflow.fulfilled, (state, { payload }) => {
      if (payload?.updateFlow?.flowId) {
        state.currentCreatedWorkflowId = payload?.updateFlow?.flowId
        state.saveDsrWorkflowStatus = DsrWorkflowSaveStatus.UPDATE_SUCCESS
      } else {
        state.saveDsrWorkflowStatus = DsrWorkflowSaveStatus.ERROR
      }
    })
    builder.addCase(fetchWorkflows.fulfilled, (state, { payload }) => {
      state.workflows = {
        list: payload.list,
        totalCount: payload.totalCount
      }
    })
    builder.addCase(fetchDetailedActiveWorkflow.fulfilled, (state, { payload }) => {
      if (payload) {
        state.activeWorkflow = payload.workflow
        state.workflowBlockConfig = payload.blockConfig
        state.stepData = payload.stepData
      }
    })
  }
})

export const {
  setActiveWorkflow,
  setActionToBeTaken,
  setActionToBeTakenBy,
  setWorkflowAutomationName,
  setRequestSubmittedEmailTemplateId,
  setEmailVerificationSuccessEmailTemplateId,
  setEmailIdVerificationAcknowledgement,
  setRequestApprovalEmailTemplateId,
  setApproveRequest,
  setSendAdditionalEmails,
  setAdditionalEmailTemplateId,
  setAdditionalEmailFields,
  setAdditionalEmailTemplateError,
  setAdditionalEmailFieldsError,
  setSendAdditionalEmailsForReportSharing,
  setAdditionalEmailFieldsForReportSharing,
  setAdditionalEmailFieldsForReportSharingError,
  setSendApprovalEmail,
  setShareReportWithDataSubject,
  setShareReportEmailTemplateId,
  setReportPasswordEmailTemplateId,
  setAutomaticallyCloseRequest,
  setSendClosureEmail,
  setClosureEmailTemplateId,
  setActiveWorkflowDefaultData,
  resetActiveDsrWorkflow,
  setRequestSubmittedEmailTemplateError,
  setEmailVerificationSuccessEmailTemplateError,
  setRequestApprovalEmailTemplateError,
  setClosureEmailTemplateError,
  setReportPasswordEmailTemplateError,
  setReportSharingEmailTemplateError,
  setWorkflowToDeleteId,
  setWorkflowToUpdateId,
  setWorkflowAutomationNameError,
  updateEmailIdVerificationStepData,
  updateRequestApprovalStepData,
  updateDataDiscoveryValidationStepData,
  updateReportGenerationStepData,
  initializeActiveWorkflow,
  updateActiveWorkflow,
  setWorkflowBlockConfig
} = dsrWorkflowAutomationSlice.actions

export default dsrWorkflowAutomationSlice.reducer
